import { CommonModule } from '@angular/common';
import { Component, Inject, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormsModule, NgForm, ReactiveFormsModule, Validators } from '@angular/forms';
import { TemplateVariable } from '../../_interfaces/email.interface';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatTooltip } from '@angular/material/tooltip';
import { DragDropModule } from '@angular/cdk/drag-drop';
import _ from 'lodash';
import { MatIcon } from '@angular/material/icon';
import { MatIconButton } from '@angular/material/button';
import { CdkTextareaAutosize } from '@angular/cdk/text-field';
import { AlertService } from '../../_services/alert.service';
import { getNewId } from '../../_utils/utils';
import { FirebaseService } from '../../_services/firebase.service';

@Component({
  selector: 'app-variable-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    DragDropModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatTooltip,
    MatIcon,
    CdkTextareaAutosize,
    MatIconButton
  ],
  templateUrl: './variable-dialog.component.html',
  styleUrl: './variable-dialog.component.scss'
})

export class VariableDialogComponent {

  @ViewChild('varForm') varForm!: NgForm;
  variableForm: FormGroup;
  varInitial: TemplateVariable;

  constructor(
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<VariableDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: VariableDialogModel,
    private alertService: AlertService,
    private fbs: FirebaseService
  ) {

    this.varInitial = this.data.mode === 'new' ? this.initiateForm(this.data.langCodes) : this.initiateForm(this.data.langCodes, data.variable);

    this.variableForm = this.fb.group({
      id: new FormControl(this.varInitial.id, Validators.required),
      name: new FormControl(this.varInitial.name, Validators.required),
      value: this.getValueFg(this.data.langCodes, this.data.variable?.value || null),
      createdBy: new FormControl(this.varInitial.createdBy),
      createdAt: new FormControl(this.varInitial.createdAt),
    })
  }

  initiateForm(langCodes: string[], variable?: TemplateVariable | null): TemplateVariable {
    return {
      id: variable ? variable.id : getNewId(),
      name: variable ? variable.name : '',
      value: variable ? variable.value : this.convertArrayToObject(langCodes),
      createdBy: variable ? variable.createdBy : this.fbs.getCurrentUserId(),
      createdAt: variable ? variable.createdAt : new Date()
    }
  }

  convertArrayToObject(array: string[]): { [key: string]: string } {
    const obj: { [key: string]: string } = {};
    for (const key of array) {
      obj[key] = "";
    }
    return obj;
  }

  objectKeys(obj: any): string[] {
    return Object.keys(obj) as string[];
  }

  get valueGroup(): FormGroup {
    return this.variableForm.get('value') as FormGroup;
  }

  getValueFg(langCodes: string[], value: { [langCode: string]: string | number } | null): FormGroup {
    const valueGroup = this.fb.group({})
    langCodes.map(code => {
      valueGroup.addControl(code, new FormControl(value && value[code] ? value[code] : '', Validators.required));
    });
    return valueGroup;
  }

  useEnValueForAll() {
    const enValue = this.variableForm.get('value')?.value['en'];
    const valueGroup = this.variableForm.get('value') as FormGroup;
    for (const key in valueGroup.controls) {
      valueGroup.controls[key].setValue(enValue);
    }
  }

  hasChanges() {
    const initial = _.cloneDeep(this.varInitial);
    const current = _.cloneDeep(this.variableForm.value);
    return !_.isEqual(initial, current);
  }

  async close() {
    if (this.hasChanges()) {
      const confirmed = await this.alertService.confirm('Alert', 'You have unsaved changes. Do you really want to discard them?', 'Discard', 'Cancel');
      if (!confirmed) {
        return;
      }
      this.dialogRef.close(null);
    } else {
      this.dialogRef.close(null);
    }
  }

  reset() {
    this.variableForm.reset();
    this.varForm.resetForm()
    this.variableForm.patchValue(this.varInitial);
  }

  update(isDelete: boolean = false) {
    let variable: TemplateVariable = this.variableForm.value;
    variable.updatedAt = new Date();
    const data = {
      variable: variable,
      mode: isDelete ? 'delete' : this.data.mode
    }
    this.dialogRef.close(data);
  }

}

export class VariableDialogModel {
  constructor(
    public mode: 'new' | 'edit',
    public langCodes: string[],
    public variable?: TemplateVariable | null
  ) { }
}